import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Offcanvas } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../../../common/types';
import { smlNavigationMessages } from '../../navigation/smlNavigationMessages';
import { linkedFileUuid } from '../filesRedirectTarget';
import { studioWorkMessages } from './studioWorkMessages';
import { studioWorkPanelMessages as messages } from './studioWorkPanelMessages';
import { WorkScreen } from './WorkScreen';
import styles from './StudioWorkPanel.module.scss';

interface StudioWorkPanelProps {
  accountId: number;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * The Work panel at the right of Studio (design navigation-v2 c 3e): the same lists as the old
 * Work page (now, failed with "Try again", finished in the last days). A link of the old Work
 * route keeps its song (`catalogItemUuid` with `state=`) and its marked file (`result=`, `reel=`).
 */
const StudioWorkPanelComponent: React.FC<StudioWorkPanelProps> = ({ accountId, isOpen, onClose }) => {
  const intl = useIntl();
  const { search } = useLocation();
  const linked = useMemo(() => {
    const params = new URLSearchParams(search);
    const song = params.get('catalogItemUuid') ?? '';
    // Only a work link (`state=`) narrows the list to one song: the Studio song alone does not.
    return { song: params.has('state') && song !== '' ? song : undefined, uuid: linkedFileUuid(search) };
  }, [search]);
  const title = intl.formatMessage(smlNavigationMessages.promoteWork);

  return (
    <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" className={styles.panel} aria-label={title}>
      <div className={styles.panelInner} data-testid="studio-work-panel">
        <div className={styles.panelHead}>
          <h2 className={styles.panelTitle}>{title}</h2>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={intl.formatMessage(messages.close)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <small className={styles.hint}>{intl.formatMessage(studioWorkMessages.queueHint)}</small>
        {isOpen && <WorkScreen accountId={accountId} catalogItemUuid={linked.song} linkedUuid={linked.uuid} />}
      </div>
    </Offcanvas>
  );
};

export const StudioWorkPanel = TypedMemo(StudioWorkPanelComponent);
